/**
 * Minimal, safe renderer for assistant text: paragraphs, lists and **bold**.
 * Builds React elements only (no dangerouslySetInnerHTML), so model output can never inject HTML.
 */

import React, { Fragment, type ReactNode } from 'react';
import { UserRound } from 'lucide-react';
import { useTranslation } from '@/contexts/LanguageContext';

const LIST_ITEM = /^\s*(\d+[.)]|[-*•])\s+/;

function renderBold(line: string): ReactNode {
  return line.split(/\*\*(.+?)\*\*/g).map((part, i) =>
    i % 2 === 1 ? (
      <strong key={i} className="font-semibold">
        {part}
      </strong>
    ) : (
      <Fragment key={i}>{part}</Fragment>
    )
  );
}

export function HelpMessageText({ text }: { text: string }) {
  const blocks = text.split(/\n{2,}/);

  return (
    <div className="flex flex-col gap-2">
      {blocks.map((block, i) => {
        const lines = block.split('\n').filter((l) => l.trim());
        const isList = lines.length > 0 && lines.every((l) => LIST_ITEM.test(l));

        if (isList) {
          const ordered = /^\s*\d/.test(lines[0]);
          const Tag = ordered ? 'ol' : 'ul';
          const start = ordered ? parseInt(lines[0].trim(), 10) : undefined;
          return (
            <Tag
              key={i}
              start={start && start > 1 ? start : undefined}
              className={`flex flex-col gap-1 pl-5 ${ordered ? 'list-decimal' : 'list-disc'}`}
            >
              {lines.map((l, j) => (
                <li key={j}>{renderBold(l.replace(LIST_ITEM, ''))}</li>
              ))}
            </Tag>
          );
        }

        return (
          <p key={i} className="whitespace-pre-wrap">
            {renderBold(block)}
          </p>
        );
      })}
    </div>
  );
}

/** A reply written by the human team, shown with a name so it is not mistaken for the AI. */
export function HelpTeamMessage({ text }: { text: string }) {
  const { t } = useTranslation();
  return (
    <div className="max-w-[92%] self-start rounded-2xl rounded-bl-md border border-primary/40 bg-primary/5 px-3.5 py-2.5 text-sm leading-relaxed">
      <span className="mb-1 flex items-center gap-1.5 text-xs font-semibold text-primary">
        <UserRound className="size-3.5" aria-hidden />
        {t('help.teamName')}
      </span>
      <HelpMessageText text={text} />
    </div>
  );
}
